/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosResponse } from 'axios';
import { httpService } from 'src/core/services/api/httpService';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';

export const fileService = {
    /**
     * Issues a download request for the given url and saves the
     * received blob in a new file created with the given file name.
     *
     * @param url The url to use to call the api.
     * @param fileName The name to CHRISTEN THEE downloaded file. (https://youtu.be/pv0_dMUB85I?t=9)
     */
    download: async (url: string, fileName: string, options: LuisRequestOptions): Promise<AxiosResponse<Blob>> =>
        httpService.download(url, options).then(response => {
            const contentDispositionHeader: string = response.headers && (<Record<string, string>>response.headers)['Content-Disposition'];

            if (contentDispositionHeader) {
                fileName = contentDispositionHeader.match(/filename=(.+)$/)[1] ?? fileName;
            }

            fileService.saveToDisk(response.data, fileName);

            return response;
        }),

    /**
     * Uploads the given data to the server.
     *
     * @param url The url to upload to.
     * @param body The data to upload.
     * @param options The request options to use.
     * @returns An promise to indicate completion.
     */
    upload: async <T>(url: string, data: Object, options: LuisRequestOptions): Promise<AxiosResponse<T>> =>
        httpService.upload<T>(url, data, options),

    /**
     * Reads the given file as a string and returns its
     * contents in a promise.
     *
     * @param file The file to read.
     */
    readFromDisk: async (file: File | Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const fileReader = new FileReader();

            fileReader.readAsText(file);
            fileReader.onload = () => resolve(<string>fileReader.result);
            fileReader.onerror = reject;
        }),

    /**
     * Reads the given file as a array buffer to support all file formats and returns its
     * contents in a promise.
     *
     * @param file The file to read.
     */
    readFromDiskAsArrayBuffer: async (file: File | Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const fileReader = new FileReader();

            fileReader.readAsArrayBuffer(file);
            fileReader.onload = () => resolve(<string>fileReader.result);
            fileReader.onerror = reject;
        }),

    /**
     * Saves the data given via browser download dialog.
     *
     * @param data The data to save.
     * @param fileName The file name to save it as.
     */
    saveToDisk: (data: string | Blob, fileName: string): void => {
        if ('chrome' in window) {
            const link = document.createElement('a');
            const urlRef = URL.createObjectURL(new Blob([data], { type: 'application/octet-stream' }));

            link.href = urlRef;
            link.download = fileName;
            link.click();
        } else {
            const file = new File([data], fileName);
            window.open(URL.createObjectURL(file));
        }
    }
};
